import type {
  AudioProcessing,
  CatalogLinking,
  IdentificationAttempt,
  Mp3Validation,
  ProcessingCheckpoint,
  ProcessingJob,
  ReconciledFieldRecord,
} from "./processingTypes";

/** One operator-facing evidence row inside an expanded checkpoint. */
export interface CheckpointEvidenceRow {
  label: string;
  value: string;
  /** Soft callout for missing instrumentation or bypass. */
  muted?: boolean;
  /** Opens Decision Audit when the timeline wires the callback. */
  action?: "decision-audit";
  /** Section header spanning the evidence list (CPT-3 Audio Processing). */
  section?: boolean;
}

const CPT3_LEGACY =
  "Not recorded on this job — audio evidence was captured before CPT-3 instrumentation";
const SEE_AUDIO_PROCESSING =
  "Not measured at Validate MP3 — see Audio Processing for the full original report";
const CPT4 =
  "Not recorded yet — requires CPT-4 playback provenance";
const CPT5 =
  "Not recorded yet — requires CPT-5 pluggable policy engine";

function present(value: unknown): value is string | number | boolean {
  if (value === null || value === undefined || value === "") return false;
  return true;
}

export function formatBytes(size: number | null | undefined): string {
  if (!present(size) || typeof size !== "number" || size < 0) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (!present(ms) || typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  const totalSec = ms / 1000;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec - minutes * 60;
  return `${minutes}:${seconds.toFixed(3).padStart(6, "0")}`;
}

export function formatSilenceMs(ms: number | null | undefined): string {
  if (!present(ms) || typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  if (ms === 0) return "None detected (0 ms)";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(3)} s (${Math.round(ms)} ms)`;
}

export function formatChannels(channels: number | null | undefined): string {
  if (!present(channels) || typeof channels !== "number") return "—";
  if (channels === 1) return "Mono (1)";
  if (channels === 2) return "Stereo (2)";
  return `${channels} channels`;
}

export function formatLufs(value: number | null | undefined): string {
  if (!present(value) || typeof value !== "number" || !Number.isFinite(value)) {
    return "—";
  }
  return `${value} LUFS`;
}

export function formatDb(value: number | null | undefined, unit = "dB"): string {
  if (!present(value) || typeof value !== "number" || !Number.isFinite(value)) {
    return "—";
  }
  return `${value} ${unit}`;
}

function row(
  label: string,
  value: string,
  muted = false,
  action?: "decision-audit",
): CheckpointEvidenceRow {
  return { label, value, muted, action };
}

function section(label: string): CheckpointEvidenceRow {
  return { label, value: "", section: true };
}

function gap(hasCpt3Nested: boolean): string {
  return hasCpt3Nested ? "—" : CPT3_LEGACY;
}

function display(value: unknown, fallback = "—"): string {
  if (!present(value)) return fallback;
  return String(value);
}

function notYet(sliceNote: string): CheckpointEvidenceRow {
  return row("Note", sliceNote, true);
}

function songTypeLabel(songType: string | null | undefined): string {
  if (songType === "kk-original") return "KrossKast Original";
  if (songType === "commercial") return "Commercial";
  return songType || "—";
}

function uploadSourceLabel(source: string | null | undefined): string {
  if (source === "sa-employee") return "Super Admin employee upload";
  if (source === "music-vendor-ma") return "Music vendor (Master Account)";
  return source || "—";
}

function uploadCompletionStatus(job: ProcessingJob): string {
  if (job.intake?.checksumStatus === "computed" || job.intakeChecksumStatus === "computed") {
    return "Complete — intake received and checksum recorded";
  }
  if (job.uploadCompletedAt || job.intake?.uploadedAt) {
    return "Complete — upload timestamp recorded";
  }
  // Only upload_initiated is pre-receipt; every other pipeline state implies upload completed.
  if (job.state && job.state !== "upload_initiated") {
    return "Complete";
  }
  return display(job.intake?.checksumStatus || job.intakeChecksumStatus, "Pending");
}

function entityResolution(
  name: string | null | undefined,
  id: string | null | undefined,
  created: boolean | null | undefined,
): string {
  if (!name && !id) return "—";
  const bits = [name || "—"];
  if (id) bits.push(id);
  if (created === true) bits.push("Created");
  else if (created === false) bits.push("Resolved existing");
  return bits.join(" · ");
}

function fieldWinningSource(
  fields: Record<string, ReconciledFieldRecord> | undefined,
  key: string,
): string {
  const f = fields?.[key];
  if (!f) return "—";
  if (!present(f.value) && f.value !== 0) return "Missing";
  return `${display(f.value)} (source: ${f.source || "—"})`;
}

function attemptOutcomeLine(a: IdentificationAttempt): string {
  const provider = a.provider || "unknown";
  const outcome = a.outcome || "—";
  const score = present(a.score) ? ` · score ${a.score}` : "";
  const err = a.error ? ` · ${a.error}` : "";
  return `${provider}: ${outcome}${score}${err}`;
}

function audioEvidence(audio: AudioProcessing | null | undefined): CheckpointEvidenceRow[] {
  if (!audio) {
    return [row("Audio processing block", "Not available on this job", true)];
  }

  const hasNested = Boolean(audio.original || audio.processed);
  const orig = audio.original || {};
  const proc = audio.processed || {};
  const mutedGap = !hasNested;

  const originalDuration = present(orig.durationMs)
    ? formatDurationMs(orig.durationMs)
    : gap(hasNested);
  const originalBitrate =
    present(orig.bitrateKbps)
      ? `${orig.bitrateKbps} kbps`
      : gap(hasNested);
  const originalSampleRate =
    present(orig.sampleRateHz)
      ? `${Number(orig.sampleRateHz).toLocaleString()} Hz`
      : gap(hasNested);
  const originalChannels = present(orig.channels)
    ? formatChannels(orig.channels)
    : gap(hasNested);
  const originalSize = present(orig.sizeBytes)
    ? formatBytes(orig.sizeBytes)
    : gap(hasNested);
  const originalLufs = present(orig.lufs)
    ? formatLufs(orig.lufs)
    : present(audio.inputLufs)
      ? formatLufs(audio.inputLufs as number)
      : present(audio.lufs)
        ? `${audio.lufs} LUFS (legacy field)`
        : gap(hasNested);
  const originalTruePeak = present(orig.truePeakDb)
    ? formatDb(orig.truePeakDb, "dBTP")
    : gap(hasNested);
  const originalChecksum =
    orig.checksumSha256 || audio.sourceIntakeChecksum || gap(hasNested);
  const leadingDetected = present(orig.leadingSilenceMs)
    ? formatSilenceMs(orig.leadingSilenceMs)
    : gap(hasNested);
  const trailingDetected = present(orig.trailingSilenceMs)
    ? formatSilenceMs(orig.trailingSilenceMs)
    : gap(hasNested);

  const recipe =
    proc.recipeId || audio.ffmpegRecipeId || "—";
  const playbackVersion = present(proc.versionNumber)
    ? `v${proc.versionNumber}`
    : present(audio.versionNumber)
      ? `v${audio.versionNumber}`
      : "—";
  const finalDuration = present(proc.durationMs)
    ? formatDurationMs(proc.durationMs)
    : formatDurationMs(audio.durationMs);
  const outputBitrate = present(proc.bitrateKbps)
    ? `${proc.bitrateKbps} kbps CBR`
    : present(audio.bitrateKbps)
      ? `${audio.bitrateKbps} kbps CBR`
      : "—";
  const outputSampleRate = present(proc.sampleRateHz)
    ? `${Number(proc.sampleRateHz).toLocaleString()} Hz`
    : present(audio.sampleRateHz ?? audio.sampleRate)
      ? `${Number(audio.sampleRateHz ?? audio.sampleRate).toLocaleString()} Hz`
      : "—";
  const outputChannels = present(proc.channels)
    ? formatChannels(proc.channels)
    : formatChannels(audio.channels ?? null);
  const outputSize = present(proc.sizeBytes)
    ? formatBytes(proc.sizeBytes)
    : gap(hasNested);
  const targetLufs = present(proc.lufsTarget)
    ? formatLufs(proc.lufsTarget)
    : present(audio.lufsTarget)
      ? formatLufs(audio.lufsTarget as number)
      : "—";
  const finalLufs = present(proc.lufsFinal)
    ? formatLufs(proc.lufsFinal)
    : gap(hasNested);
  const gainApplied = present(proc.gainAppliedDb)
    ? formatDb(proc.gainAppliedDb, "dB")
    : gap(hasNested);
  const finalTruePeak = present(proc.truePeakDb)
    ? formatDb(proc.truePeakDb, "dBTP")
    : gap(hasNested);
  const leadingTrimmed = present(proc.leadingSilenceTrimmedMs)
    ? formatSilenceMs(proc.leadingSilenceTrimmedMs)
    : gap(hasNested);
  const trailingTrimmed = present(proc.trailingSilenceTrimmedMs)
    ? formatSilenceMs(proc.trailingSilenceTrimmedMs)
    : gap(hasNested);
  const silenceThreshold =
    present(proc.silenceTrimThresholdDb) || present(audio.silenceTrimThresholdDb)
      ? `${proc.silenceTrimThresholdDb ?? audio.silenceTrimThresholdDb} dBFS`
      : "—";
  const loudnessRange = present(proc.loudnessRangeTargetLu)
    ? `${proc.loudnessRangeTargetLu} LU (LRA target)`
    : gap(hasNested);
  const outputValidation =
    proc.outputValidation === "pass" || audio.status === "complete"
      ? "Pass — KrossKast Processed MP3 published"
      : proc.outputValidation || audio.status || "—";
  const outputChecksum =
    proc.checksumSha256 || audio.checksumApproved || "—";
  const processingDuration =
    present(proc.processingDurationMs) || present(audio.processingDurationMs)
      ? `${Math.round(Number(proc.processingDurationMs ?? audio.processingDurationMs)).toLocaleString()} ms`
      : gap(hasNested);
  const objectLocation =
    proc.approvedObjectKey || audio.approvedObjectKey || "—";
  const limiting =
    proc.limitingLikely === true
      ? "Likely — original true peak exceeded true-peak target"
      : proc.limitingLikely === false
        ? "Not indicated by measured true-peak vs target"
        : gap(hasNested);
  const adopted =
    (proc.adoptedExisting ?? audio.adoptedExisting)
      ? "Yes — reused existing KrossKast MP3 (idempotent replay; no new encode)"
      : "No — new KrossKast MP3 created";

  return [
    row(
      "What this checkpoint produced",
      "New KrossKast MP3 created — the trusted playback file (not the original upload)",
    ),
    section("Original MP3 (before KrossKast processing)"),
    row("Original duration", originalDuration, mutedGap && !present(orig.durationMs)),
    row("Original bitrate", originalBitrate, mutedGap && !present(orig.bitrateKbps)),
    row("Original sample rate", originalSampleRate, mutedGap && !present(orig.sampleRateHz)),
    row("Original channels", originalChannels, mutedGap && !present(orig.channels)),
    row("Original file size", originalSize, mutedGap && !present(orig.sizeBytes)),
    row("Original LUFS", originalLufs, mutedGap && !present(orig.lufs) && !present(audio.inputLufs)),
    row("Original true peak", originalTruePeak, mutedGap && !present(orig.truePeakDb)),
    row("Original checksum (SHA-256)", String(originalChecksum), mutedGap && !orig.checksumSha256 && !audio.sourceIntakeChecksum),
    row("Leading silence detected", leadingDetected, mutedGap && !present(orig.leadingSilenceMs)),
    row("Trailing silence detected", trailingDetected, mutedGap && !present(orig.trailingSilenceMs)),
    ...(present(orig.codecName)
      ? [row("Original codec", String(orig.codecName))]
      : []),
    section("KrossKast Processed MP3"),
    row("Processing recipe / version", String(recipe)),
    row("Playback file version", playbackVersion),
    row("Final duration", finalDuration),
    row("Output bitrate", outputBitrate),
    row("Output sample rate", outputSampleRate),
    row("Output channels", outputChannels),
    row("Output file size", outputSize, mutedGap && !present(proc.sizeBytes)),
    row("Target LUFS", targetLufs),
    row("Final LUFS", finalLufs, mutedGap && !present(proc.lufsFinal)),
    row("Gain applied", gainApplied, mutedGap && !present(proc.gainAppliedDb)),
    row("Final true peak", finalTruePeak, mutedGap && !present(proc.truePeakDb)),
    row("Leading silence trimmed", leadingTrimmed, mutedGap && !present(proc.leadingSilenceTrimmedMs)),
    row("Trailing silence trimmed", trailingTrimmed, mutedGap && !present(proc.trailingSilenceTrimmedMs)),
    row("Silence threshold used", silenceThreshold),
    row("Loudness range target (LRA)", loudnessRange, mutedGap && !present(proc.loudnessRangeTargetLu)),
    row("True-peak limiting", limiting, mutedGap && proc.limitingLikely == null),
    row("Output validation", outputValidation),
    row("Output checksum (SHA-256)", String(outputChecksum)),
    row("Processing duration", processingDuration, mutedGap && !present(proc.processingDurationMs) && !present(audio.processingDurationMs)),
    row("Playback object location", String(objectLocation)),
    row("Encode outcome", adopted),
  ];
}

function catalogEvidence(
  linking: CatalogLinking | null | undefined,
  job: ProcessingJob,
): CheckpointEvidenceRow[] {
  if (!linking) {
    return [row("Catalog linking block", "Not available on this job", true)];
  }
  const albumLine =
    linking.albumId || linking.albumTitle
      ? entityResolution(linking.albumTitle, linking.albumId, linking.albumCreated)
      : "Single — no album linked";
  return [
    row(
      "Artist",
      entityResolution(linking.artistName, linking.artistId, linking.artistCreated),
    ),
    row("Album", albumLine),
    row(
      "Record Label",
      entityResolution(
        linking.recordLabelName,
        linking.recordLabelId,
        linking.recordLabelCreated,
      ),
    ),
    row("Canonical title", linking.canonicalTitle || "—"),
    row("Artist ID", linking.artistId || "—"),
    row("Album ID", linking.albumId || "—"),
    row("Record Label ID", linking.recordLabelId || "—"),
    row(
      "Confidence used at linking",
      present(linking.confidenceAtCreation)
        ? String(linking.confidenceAtCreation)
        : "Not recorded yet — persisted from CPT-2 onward",
      !present(linking.confidenceAtCreation),
    ),
    row(
      "Track created or reused",
      job.seamOutcome
        ? String(job.seamOutcome)
        : job.trackId
          ? display(job.checkpoints?.find((c) => c.key === "track-finalized")?.detail)
          : "Not yet — set at Track Record Finalized",
    ),
    row("Track ID", job.trackId || "—"),
    row(
      "Ambiguity result",
      Array.isArray(linking.unresolved) && linking.unresolved.length
        ? linking.unresolved.join(", ")
        : linking.status === "resolved"
          ? "None — resolved"
          : linking.status || "—",
    ),
    row("Source Song Processing ID", job.jobId),
    row("Match method", linking.matchMethod || "—"),
    row(
      "Notes",
      Array.isArray(linking.notes) && linking.notes.length
        ? linking.notes.join("; ")
        : typeof linking.notes === "string"
          ? linking.notes
          : "None",
    ),
    row(
      "Correction / relink history",
      Array.isArray(job.metadataIdentification?.phases?.reconciliation?.corrections) &&
        (job.metadataIdentification?.phases?.reconciliation?.corrections?.length ?? 0) > 0
        ? `${job.metadataIdentification?.phases?.reconciliation?.corrections?.length} correction(s) recorded — see Decision Audit`
        : "None recorded on this job",
    ),
    row("Resolved at", linking.resolvedAt || "—"),
  ];
}

function mp3Evidence(
  v: Mp3Validation | null | undefined,
  checkpoint: ProcessingCheckpoint | undefined,
  job: ProcessingJob,
): CheckpointEvidenceRow[] {
  if (!v) {
    const sampleFromDetail = checkpoint?.detail?.match(/(\d+)\s*Hz/i)?.[1];
    return [
      row(
        "Structured validation block",
        "Not recorded yet — jobs validated before CPT-2 only have checkpoint detail",
        true,
      ),
      row("Frame validation detail", checkpoint?.detail || "—"),
      row(
        "Sample rate (from checkpoint detail)",
        sampleFromDetail ? `${Number(sampleFromDetail).toLocaleString()} Hz` : "—",
      ),
      row("Original file size", formatBytes(job.intake?.sizeBytes ?? job.intakeSizeBytes)),
      row("Original duration", SEE_AUDIO_PROCESSING, true),
      row("CBR or VBR", SEE_AUDIO_PROCESSING, true),
      row("Decode validation", SEE_AUDIO_PROCESSING, true),
      row("Duration-policy result", SEE_AUDIO_PROCESSING, true),
      row(
        "Overall technical-validation result",
        checkpoint?.status === "pass"
          ? "Pass"
          : checkpoint?.status === "fail"
            ? "Fail"
            : checkpoint?.status || "—",
      ),
    ];
  }

  const bitrateModeLabel =
    v.bitrateMode === "frame-header"
      ? "Frame-header only (full CBR/VBR scan not recorded at Validate MP3)"
      : display(v.bitrateMode);

  return [
    row("Format", display(v.format)),
    row("Codec", display(v.codec)),
    row("MPEG version", display(v.mpegVersion)),
    row("Layer", present(v.layer) ? `Layer ${v.layer}` : "—"),
    row("CBR or VBR", bitrateModeLabel, v.bitrateMode === "frame-header"),
    row(
      "Original bitrate (frame header)",
      present(v.bitrateKbps) ? `${v.bitrateKbps} kbps` : "—",
    ),
    row(
      "Sample rate",
      present(v.sampleRateHz) ? `${Number(v.sampleRateHz).toLocaleString()} Hz` : "—",
    ),
    row(
      "Channel configuration",
      present(v.channelMode)
        ? `${v.channelMode}${present(v.channels) ? ` (${v.channels})` : ""}`
        : formatChannels(v.channels ?? null),
    ),
    row(
      "Original duration",
      present(v.originalDurationMs)
        ? formatDurationMs(v.originalDurationMs)
        : SEE_AUDIO_PROCESSING,
      !present(v.originalDurationMs),
    ),
    row(
      "Original file size",
      formatBytes(v.originalFileSizeBytes ?? job.intake?.sizeBytes ?? job.intakeSizeBytes),
    ),
    row(
      "Frame validation",
      v.frameValidation === "pass"
        ? "Pass"
        : display(v.frameValidation || (v.ok ? "Pass" : v.reason)),
    ),
    row(
      "Consecutive frames validated",
      v.consecutiveFramesValidated === true
        ? "Yes"
        : v.consecutiveFramesValidated === false
          ? "No (prefix ended mid-stream)"
          : "—",
    ),
    row(
      "Decode validation",
      v.decodeValidation === "frame-header-only"
        ? "Frame-header only — full decode not performed at Validate MP3"
        : display(v.decodeValidation),
      v.decodeValidation === "frame-header-only",
    ),
    row(
      "Corruption / truncation result",
      v.corruptionDetected === true
        ? "Corruption or truncation detected"
        : v.corruptionDetected === false
          ? "None detected in inspected prefix"
          : display(v.reason, "—"),
    ),
    row(
      "Duration-policy result",
      present(v.durationPolicyResult)
        ? String(v.durationPolicyResult)
        : SEE_AUDIO_PROCESSING,
      !present(v.durationPolicyResult),
    ),
    row(
      "Overall technical-validation result",
      v.overallResult === "pass"
        ? "Pass"
        : v.overallResult === "fail"
          ? "Fail"
          : display(v.overallResult || (v.ok ? "Pass" : "Fail")),
    ),
    row(
      "Bytes inspected",
      present(v.inspectedBytes)
        ? `${formatBytes(v.inspectedBytes as number)} of prefix (cap ${formatBytes(v.inspectedPrefixBytes as number | null)})`
        : "—",
    ),
    row("Validated at", display(v.validatedAt)),
  ];
}

function metadataEvidence(
  job: ProcessingJob,
  checkpoint: ProcessingCheckpoint | undefined,
  opts?: { canOpenDecisionAudit?: boolean },
): CheckpointEvidenceRow[] {
  const mid = job.metadataIdentification;
  const selected = mid?.phases?.reconciliation?.selected;
  const fields = mid?.phases?.reconciliation?.fields;
  const confidence = mid?.phases?.confidence;
  const attempts = mid?.phases?.identification?.attempts || [];
  const filenameParse = job.metadataDiscovery?.sources?.filenameParse as
    | { featuredArtistHints?: string[]; title?: string; artist?: string }
    | undefined;
  const featured =
    filenameParse?.featuredArtistHints && filenameParse.featuredArtistHints.length
      ? filenameParse.featuredArtistHints.join(", ")
      : null;

  const missing: string[] = [];
  for (const key of ["title", "artist", "album", "recordLabelName", "releaseYear", "isrc", "mediaSource"]) {
    const f = fields?.[key];
    const sel = selected?.[key as keyof typeof selected];
    if ((!f || !present(f.value)) && !present(sel)) missing.push(key);
  }

  const conflictKeys = fields
    ? Object.entries(fields)
        .filter(([, f]) => f && (f.conflict || f.strongConflict))
        .map(([k, f]) => `${k}${f.strongConflict ? " (strong)" : ""}`)
    : [];

  const thresholds = confidence?.thresholds;
  const autoThreshold = thresholds?.autoProceed;
  const reviewThreshold = thresholds?.review;

  const decisionOutcome = mid?.decision?.outcome;
  const autoOrReview =
    decisionOutcome === "proceed"
      ? "Auto-proceed"
      : decisionOutcome === "review"
        ? "Human review"
        : mid?.decision?.businessSummary || checkpoint?.detail || "—";

  const rows: CheckpointEvidenceRow[] = [
    row("Final selected Title", display(selected?.title ?? job.discoveredTitle)),
    row("Primary Artist", display(selected?.artist ?? job.discoveredArtist)),
    row(
      "Featured Artists",
      featured || "None recorded (filename parse may not have featured markers)",
      !featured,
    ),
    row("Album", display(selected?.album ?? job.discoveredAlbum)),
    row("Record Label", display(selected?.recordLabelName)),
    row(
      "Release Year",
      selected?.releaseYear != null ? String(selected.releaseYear) : "—",
    ),
    row(
      "Source",
      display(
        selected?.mediaSource ??
          (job as { mediaSource?: string | null }).mediaSource,
      ),
    ),
    row("ISRC", display(selected?.isrc ?? job.isrcHint)),
    row(
      "Filename interpretation",
      filenameParse?.title || filenameParse?.artist
        ? `Title: ${display(filenameParse.title)}; Artist: ${display(filenameParse.artist)}`
        : display(mid?.phases?.extraction?.summary, "—"),
    ),
    row(
      "Providers attempted",
      attempts.length
        ? attempts.map((a) => a.provider || "unknown").join(", ")
        : display(mid?.phases?.identification?.skipReason, "None / skipped"),
    ),
    row(
      "Provider outcomes",
      attempts.length ? attempts.map(attemptOutcomeLine).join(" · ") : "—",
    ),
    row("Winning source — Title", fieldWinningSource(fields, "title")),
    row("Winning source — Primary Artist", fieldWinningSource(fields, "artist")),
    row("Winning source — Album", fieldWinningSource(fields, "album")),
    row("Winning source — Record Label", fieldWinningSource(fields, "recordLabelName")),
    row("Winning source — Release Year", fieldWinningSource(fields, "releaseYear")),
    row("Winning source — ISRC", fieldWinningSource(fields, "isrc")),
    row(
      "Conflicts",
      conflictKeys.length ? conflictKeys.join(", ") : "None recorded",
    ),
    row(
      "Missing values",
      missing.length ? missing.join(", ") : "None among core fields",
    ),
    row(
      "Final confidence",
      confidence?.score != null
        ? `${confidence.score}${confidence.band ? ` (${confidence.band})` : ""}`
        : "—",
    ),
    row(
      "Threshold at processing time",
      present(autoThreshold) || present(reviewThreshold)
        ? `Auto-proceed ≥ ${display(autoThreshold)}; Review band ≤ ${display(reviewThreshold)}`
        : "—",
    ),
    row("Auto-proceed or human-review result", autoOrReview),
  ];

  if (opts?.canOpenDecisionAudit) {
    rows.push(
      row(
        "Decision Audit",
        "Open Decision Audit for full provider evidence and correction history",
        false,
        "decision-audit",
      ),
    );
  } else {
    rows.push(
      row(
        "Decision Audit",
        "Decision Audit unavailable for this job (no metadata identification block yet)",
        true,
      ),
    );
  }

  return rows;
}

/**
 * Build operator-facing evidence rows for one checkpoint from the job payload
 * (CPT-2 enrichment). Missing measurements are called out explicitly rather
 * than inventing values.
 */
export function buildCheckpointEvidence(
  key: string,
  job: ProcessingJob,
  checkpoint: ProcessingCheckpoint | undefined,
  opts?: { uploaderLabel?: string; canOpenDecisionAudit?: boolean },
): CheckpointEvidenceRow[] {
  const intake = job.intake;
  const rows: CheckpointEvidenceRow[] = [];

  switch (key) {
    case "upload-received":
      rows.push(
        row("Original filename", intake?.originalFilename || job.originalFilename || "—"),
        row("Uploader", opts?.uploaderLabel || "—"),
        row("Upload source", uploadSourceLabel(job.uploadSource)),
        row("Upload timestamp", intake?.uploadedAt || job.uploadCompletedAt || "—"),
        row("Original file size", formatBytes(intake?.sizeBytes ?? job.intakeSizeBytes)),
        row(
          "Intake object reference",
          intake?.processingObjectKey || job.intakeProcessingObjectKey || "—",
        ),
        row(
          "Source checksum",
          intake?.checksumIntake || job.intakeChecksumIntake || "—",
        ),
        row("Checksum status", intake?.checksumStatus || job.intakeChecksumStatus || "—"),
        row("Upload completion status", uploadCompletionStatus(job)),
        row(
          "Upload completion duration",
          present(job.uploadDurationMs) ? `${job.uploadDurationMs} ms` : "—",
        ),
        row("Content type", intake?.contentType || "—"),
      );
      break;

    case "songtype-gate":
      rows.push(
        row("Song type", songTypeLabel(job.songType)),
        row("How the type was selected", "Operator selection at upload (Song Uploader)"),
        row("Who selected it", opts?.uploaderLabel || "—"),
        row(
          "Pipeline policy path",
          job.songType === "kk-original"
            ? "KrossKast Original — house-label / auto-create catalog rules"
            : job.songType === "commercial"
              ? "Commercial — identification providers + resolve-or-create catalog rules"
              : "—",
        ),
      );
      break;

    case "processing-bucket":
      rows.push(
        row(
          "Trust zone",
          "Still the untrusted Processing area — not the KrossKast Playback MP3",
        ),
        row(
          "Processing object / path",
          intake?.processingObjectKey || job.intakeProcessingObjectKey || "—",
        ),
        row(
          "Source checksum",
          intake?.checksumIntake || job.intakeChecksumIntake || "—",
        ),
        row(
          "Object availability",
          intake?.processingObjectKey || job.intakeProcessingObjectKey
            ? "Yes — intake object key recorded"
            : "—",
        ),
        row(
          "Intake status",
          checkpoint?.status === "pass"
            ? "Pass — ready for pipeline"
            : checkpoint?.status || "—",
        ),
      );
      break;

    case "validate-mp3":
      rows.push(...mp3Evidence(job.mp3Validation, checkpoint, job));
      break;

    case "malware-scan":
      rows.push(
        row("Scanner / provider", job.malwareScan?.scanner || "—"),
        row("Scan time", job.malwareScan?.scannedAt || "—"),
        row(
          "Scanner / signature version",
          job.malwareScan?.signatureVersion ||
            "Not recorded yet — GuardDuty S3 malware tag does not return signature DB version",
          !job.malwareScan?.signatureVersion,
        ),
        row("Result", job.malwareScan?.result || checkpoint?.detail || "—"),
        row("Status", job.malwareScan?.status || "—"),
        row(
          "Threat details",
          job.malwareScan?.threatDetails ||
            (job.malwareScan?.status === "infected"
              ? display(job.malwareScan?.result)
              : "Not applicable"),
          job.malwareScan?.status !== "infected" && !job.malwareScan?.threatDetails,
        ),
        row(
          "Quarantine action",
          job.malwareScan?.quarantineAction ||
            (job.malwareScan?.status === "infected"
              ? "Quarantine path (pipeline)"
              : "Not applicable"),
        ),
      );
      break;

    case "metadata-discovery":
      rows.push(...metadataEvidence(job, checkpoint, opts));
      break;

    case "audio-processing":
      rows.push(...audioEvidence(job.audioProcessing));
      break;

    case "catalog-linking":
      rows.push(...catalogEvidence(job.catalogLinking, job));
      break;

    case "policy-check": {
      const pc = job.policyCheck;
      rows.push(
        row(
          "Policy governance engine",
          "Not yet implemented — development bypass (CPT-5)",
        ),
        row(
          "Result",
          pc?.status === "bypassed" || checkpoint?.status === "skipped"
            ? "Bypassed — Policy Gate Not Yet Implemented"
            : pc?.status || checkpoint?.status || "—",
        ),
        row(
          "Bypass reason",
          pc?.detail ||
            checkpoint?.detail ||
            "Policy Gate Not Yet Implemented",
        ),
        row("Environment", pc?.stage || "—"),
        row("Configuration / mode", pc?.mode || "—"),
        row(
          "Approval without policy evaluation",
          pc?.approvedWithoutPolicyEvaluation
            ? "Yes — approved under temporary development bypass"
            : "—",
        ),
        row(
          "Audit reference",
          "Recorded on APPROVED audit (approvedWithoutPolicyEvaluation / policyEvaluation)",
        ),
        row(
          "Non-production warning",
          "Staging/dev may bypass. Production remains fail-closed until CPT-5.",
          true,
        ),
        row("Reason code", pc?.reasonCode || checkpoint?.reasonCode || "—"),
        notYet(CPT5),
      );
      break;
    }

    case "track-finalized": {
      const linking = job.catalogLinking;
      rows.push(
        row("Track ID", job.trackId || "—"),
        row(
          "Created or updated",
          job.seamOutcome ||
            checkpoint?.detail ||
            "—",
        ),
        row(
          "Version",
          present(job.audioProcessing?.versionNumber)
            ? `Playback file v${job.audioProcessing?.versionNumber}`
            : "—",
        ),
        row(
          "Current KrossKast Playback MP3 pointer",
          job.approvedArtifactVersionId || "—",
        ),
        row("Source Song Processing ID", job.jobId),
        row(
          "Artist relationship",
          entityResolution(linking?.artistName, linking?.artistId, linking?.artistCreated),
        ),
        row(
          "Album relationship",
          linking?.albumId || linking?.albumTitle
            ? entityResolution(linking?.albumTitle, linking?.albumId, linking?.albumCreated)
            : "Single — no album",
        ),
        row(
          "Record Label relationship",
          entityResolution(
            linking?.recordLabelName,
            linking?.recordLabelId,
            linking?.recordLabelCreated,
          ),
        ),
        row(
          "Projection updated",
          job.trackId
            ? "Yes — job carries trackId / lineage for list projection"
            : "Not yet",
        ),
        row(
          "Audit event written",
          job.decision === "approved" || checkpoint?.status === "pass"
            ? "Yes — APPROVED / finalize audit on pass"
            : checkpoint?.status || "—",
        ),
      );
      break;
    }

    case "approved-artifact-created":
      rows.push(
        row(
          "What this is",
          "Newly generated KrossKast Processed MP3 — the trusted playback file, not the original uploaded source",
        ),
        row("Playback version ID", job.approvedArtifactVersionId || "—"),
        row("Track ID", job.trackId || "—"),
        row(
          "Playback file version",
          present(job.audioProcessing?.versionNumber)
            ? `v${job.audioProcessing?.versionNumber}`
            : "—",
        ),
        row("Playback object location", job.audioProcessing?.approvedObjectKey || "—"),
        row("Checksum", job.audioProcessing?.checksumApproved || "—"),
        row("Duration", formatDurationMs(job.audioProcessing?.durationMs)),
        row(
          "Bitrate",
          present(job.audioProcessing?.bitrateKbps)
            ? `${job.audioProcessing?.bitrateKbps} kbps`
            : "—",
        ),
        row(
          "Sample rate",
          present(job.audioProcessing?.sampleRateHz ?? job.audioProcessing?.sampleRate)
            ? `${Number(job.audioProcessing?.sampleRateHz ?? job.audioProcessing?.sampleRate).toLocaleString()} Hz`
            : "—",
        ),
        row(
          "Channels",
          formatChannels(job.audioProcessing?.channels ?? null),
        ),
        row("Processing recipe / version", job.audioProcessing?.ffmpegRecipeId || "—"),
        row(
          "Immutable status",
          job.approvedArtifactVersionId
            ? "Write-once playback version record (immutable after create)"
            : "—",
        ),
        row(
          "Previous playback version",
          job.reprocessOfJobId
            ? `Reprocess of ${job.reprocessOfJobId} — prior active versions superseded at finalize`
            : "None — first version for this lineage (or not recorded on job)",
        ),
        row("Created from Song Processing", job.jobId),
        row("Checkpoint detail", checkpoint?.detail || "—"),
        row("Provenance / recipe lineage detail", CPT4, true),
      );
      break;

    case "ready-for-playback":
      rows.push(
        row(
          "Live Ready for Playback",
          job.playbackIntegrity?.status === "ready_for_playback"
            ? "Ready for Playback (live integrity OK)"
            : job.playbackIntegrity?.status === "integrity_issue"
              ? "Integrity Issue — not live Ready for Playback"
              : job.playbackIntegrity?.status === "not_applicable"
                ? "Not applicable (standalone / no living Song)"
                : "Live integrity not enriched on this response",
          job.playbackIntegrity?.status === "integrity_issue",
        ),
      );
      if (
        job.playbackIntegrity?.status === "integrity_issue" &&
        Array.isArray(job.playbackIntegrity.failures)
      ) {
        for (const failure of job.playbackIntegrity.failures) {
          rows.push(
            row(
              failure?.code || "integrity-failure",
              failure?.message || "—",
              true,
            ),
          );
        }
      }
      rows.push(
        row("Track ID", job.trackId || "—"),
        row(
          "Current Details job",
          job.isCurrentPlaybackJob === true
            ? "Yes"
            : job.isCurrentPlaybackJob === false
              ? "No — superseded (History)"
              : "—",
        ),
        row(
          "Current playback file version",
          present(job.audioProcessing?.versionNumber)
            ? `v${job.audioProcessing?.versionNumber}`
            : job.approvedArtifactVersionId || "—",
        ),
        row(
          "Playable status",
          job.audioProcessing?.approvedObjectKey
            ? "KrossKast Playback MP3 recorded"
            : "—",
        ),
        row(
          "Playback eligibility",
          job.decision === "approved" && job.audioProcessing?.approvedObjectKey
            ? "Eligible — approved decision with KrossKast Playback MP3"
            : display(job.decision, "—"),
        ),
        row(
          "Catalog visibility",
          job.linkedSong?.songStatus === "active"
            ? "Active in catalog"
            : job.linkedSong?.songStatus
              ? `Linked song status: ${job.linkedSong.songStatus}`
              : "Not recorded on this job beyond Track link",
          !job.linkedSong?.songStatus,
        ),
        row(
          "Programming availability",
          "Not recorded yet — programming eligibility is outside this job payload",
          true,
        ),
        row("Final decision", job.decision || "—"),
        row(
          "Non-blocking warnings",
          job.metadataIdentification?.reporting?.reasons?.length
            ? job.metadataIdentification.reporting.reasons.join("; ")
            : "None recorded",
        ),
        row(
          "ISRC reporting status",
          job.metadataIdentification?.reporting?.status ||
            (selectedIsrc(job) ? "ISRC present — reporting pipeline status not recorded" : "—"),
          !job.metadataIdentification?.reporting?.status,
        ),
        row(
          "Policy status",
          job.policyCheck?.approvedWithoutPolicyEvaluation ||
            checkpoint?.status === "skipped" ||
            job.policyCheck?.status === "bypassed"
            ? "Bypassed — Policy Gate Not Yet Implemented"
            : job.policyCheck?.status || "—",
        ),
      );
      break;

    default:
      rows.push(row("Detail", checkpoint?.detail || "—"));
  }

  if (checkpoint?.reasonCode) {
    rows.push(row("Machine reason code", checkpoint.reasonCode));
  }

  return rows;
}

function selectedIsrc(job: ProcessingJob): string | null {
  const isrc = job.metadataIdentification?.phases?.reconciliation?.selected?.isrc;
  if (typeof isrc === "string" && isrc) return isrc;
  if (typeof job.isrcHint === "string" && job.isrcHint) return job.isrcHint;
  return null;
}

/** One-line collapsed summary under the badge. */
export function checkpointSummaryLine(
  key: string,
  job: ProcessingJob,
  checkpoint: ProcessingCheckpoint | undefined,
): string {
  if (checkpoint?.detail) return checkpoint.detail;
  if (key === "policy-check" && job.policyCheck?.detail) {
    return job.policyCheck.detail;
  }
  if (key === "validate-mp3" && job.mp3Validation?.bitrateKbps) {
    return `mp3 frame ok (${job.mp3Validation.sampleRateHz}Hz, ${job.mp3Validation.bitrateKbps}kbps)`;
  }
  return "No detail recorded yet";
}
